// src/hooks/useOrganizationMetrics.js
// Hook for fetching cached organization metrics with stale-while-revalidate pattern
// Part of Tier 2 Performance Optimization

import { useState, useEffect, useCallback } from 'react';
import { db, functions } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Staleness threshold: 15 minutes
const STALE_THRESHOLD_MS = 15 * 60 * 1000;

/**
 * Custom hook for organization metrics with caching
 * 
 * Features:
 * - Immediate display of cached metrics
 * - Real-time updates via Firestore listener
 * - Staleness indicator
 * - Manual refresh capability
 * - Background revalidation when stale
 * 
 * @param {string} organizationId - The organization ID to fetch metrics for
 * @returns {Object} - Metrics data, loading state, and utility functions
 */
const useOrganizationMetrics = (organizationId) => {
    // Core state
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Metadata state
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isStale, setIsStale] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Check if data is stale
    const checkStaleness = useCallback((timestamp) => {
        if (!timestamp) return true;
        const lastUpdateTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const age = Date.now() - lastUpdateTime.getTime();
        return age > STALE_THRESHOLD_MS;
    }, []);
    
    // Manual refresh function
    const refreshMetrics = useCallback(async () => {
        if (!organizationId || isRefreshing) return;
        
        setIsRefreshing(true);
        setError(null);
        
        try {
            const recalculateOrgMetrics = httpsCallable(functions, 'recalculateOrgMetrics');
            await recalculateOrgMetrics({ organizationId });
            // Firestore listener will pick up the update automatically
        } catch (err) {
            console.error('[useOrganizationMetrics] Refresh failed:', err);
            setError(`Failed to refresh: ${err.message}`);
        } finally {
            setIsRefreshing(false);
        }
    }, [organizationId, isRefreshing]);
    
    // Subscribe to metrics updates
    useEffect(() => {
        if (!organizationId) {
            setMetrics(null);
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setError(null);
        
        const metricsRef = doc(db, 'organizationMetrics', organizationId);
        
        // Real-time listener for cached metrics
        const unsubscribe = onSnapshot(
            metricsRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setMetrics(data);
                    setLastUpdated(data.lastUpdated);
                    setIsStale(checkStaleness(data.lastUpdated));
                    setLoading(false);
                    
                    console.log('[useOrganizationMetrics] Loaded cached metrics:', {
                        orgId: organizationId,
                        lastUpdated: data.lastUpdated?.toDate?.().toISOString(),
                        isStale: checkStaleness(data.lastUpdated)
                    });
                } else {
                    // No cached metrics yet - trigger initial calculation
                    console.log('[useOrganizationMetrics] No cached metrics found, triggering calculation');
                    setMetrics(null);
                    setIsStale(true);
                    setLoading(false);
                    
                    // Auto-trigger initial calculation (don't await)
                    refreshMetrics().catch(console.error);
                }
            },
            (err) => {
                console.error('[useOrganizationMetrics] Listener error:', err);
                setError(`Failed to load metrics: ${err.message}`);
                setLoading(false);
            }
        );
        
        return () => unsubscribe();
    }, [organizationId, checkStaleness, refreshMetrics]);
    
    // Periodically check staleness
    useEffect(() => {
        if (!lastUpdated) return;
        
        const interval = setInterval(() => {
            setIsStale(checkStaleness(lastUpdated));
        }, 60000); // Check every minute
        
        return () => clearInterval(interval);
    }, [lastUpdated, checkStaleness]);
    
    // Format last updated for display
    const getLastUpdatedDisplay = useCallback(() => {
        if (!lastUpdated) return 'Never';
        
        const date = lastUpdated.toDate ? lastUpdated.toDate() : new Date(lastUpdated);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, [lastUpdated]);
    
    return {
        // Core data
        metrics,
        loading,
        error,
        
        // Derived data (convenience accessors)
        currentMonth: metrics?.currentMonth || null,
        monthlyMetrics: metrics?.monthlyMetrics || {},
        topClients: metrics?.topClients || [],
        topFCFOs: metrics?.topFCFOs || [],
        decliningAlerts: metrics?.decliningEngagementAlerts || [],
        counters: metrics?.counters || {},
        
        // Metadata
        lastUpdated,
        lastUpdatedDisplay: getLastUpdatedDisplay(),
        isStale,
        isRefreshing,
        
        // Actions
        refreshMetrics
    };
};

export default useOrganizationMetrics;